// src/components/grammarQuest/WordCollection.jsx
// Drawer de la colección de palabras desbloqueadas — con pronunciación TTS
import React, { useState } from 'react';
import { X, Volume2, ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
import { COLLECTION_CATEGORIES, WORD_EXAMPLES, GRAMMAR_QUEST_QUESTIONS } from '../../data/grammarQuestData';

// Agrupar palabras coleccionadas por categoría
function groupByCategory(collection) {
  const allQ = GRAMMAR_QUEST_QUESTIONS.A2;
  const groups = {};

  collection.forEach(word => {
    const q = allQ.find(q => q.collected_word === word);
    if (!q) return;
    const cat = q.category;
    if (!groups[cat]) groups[cat] = [];
    if (!groups[cat].includes(word)) groups[cat].push(word);
  });

  return groups;
}

function speak(text) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = 'en-US';
  utt.rate = 0.82;
  utt.pitch = 1.0;
  window.speechSynthesis.speak(utt);
}

function WordCard({ word }) {
  const info = WORD_EXAMPLES[word] || {};
  const allQ = GRAMMAR_QUEST_QUESTIONS.A2;
  const q = allQ.find(q => q.collected_word === word);
  const cat = q ? COLLECTION_CATEGORIES[q.category] : null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 space-y-2 animate-in fade-in zoom-in-95 duration-200">
      {/* Header de tarjeta */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black text-slate-800 text-base">{word}</span>
            {info.phonetic && (
              <span className="text-[10px] font-mono text-slate-400">{info.phonetic}</span>
            )}
          </div>
          {cat && (
            <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border ${cat.color} mt-0.5 inline-block`}>
              {cat.emoji} {cat.label}
            </span>
          )}
        </div>
        <button
          onClick={() => speak(word)}
          className="shrink-0 p-2 rounded-full bg-violet-50 border border-violet-200 text-violet-500 hover:bg-violet-100 active:scale-90 transition-all"
          aria-label={`Pronunciar ${word}`}
        >
          <Volume2 size={14} />
        </button>
      </div>

      {/* Frase ejemplo */}
      {info.example && (
        <div className="bg-slate-50 rounded-xl px-3 py-2">
          <p className="text-xs font-semibold text-slate-700 leading-relaxed">
            "{info.example}"
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5 italic">{info.exampleEs}</p>
          <button
            onClick={() => speak(info.example)}
            className="mt-1.5 flex items-center gap-1 text-[10px] font-bold text-violet-500 hover:text-violet-700 transition-colors"
          >
            <Volume2 size={10} /> Escuchar frase
          </button>
        </div>
      )}
    </div>
  );
}

export default function WordCollection({ collection, onClose, totalQuestions }) {
  const grouped = groupByCategory(collection);
  const [expandedCat, setExpandedCat] = useState(null);
  const collectedCount = collection.length;
  const progressPct = totalQuestions > 0 ? Math.round((collectedCount / totalQuestions) * 100) : 0;

  return (
    // Overlay
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(15,10,40,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Drawer */}
      <div
        className="w-full max-w-md rounded-t-[2.5rem] overflow-hidden animate-in slide-in-from-bottom-5 duration-400"
        style={{
          background: 'linear-gradient(160deg, #1e1b4b 0%, #312e81 40%, #2e1065 100%)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Handle + Cerrar */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-violet-500/30 border border-violet-400/30 flex items-center justify-center">
              <BookOpen size={18} className="text-violet-300" />
            </div>
            <div>
              <h2 className="text-white font-black text-base leading-tight">Mi Colección</h2>
              <p className="text-violet-300 text-[11px] font-bold">Grammar Word Quest · A2</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-full bg-white/10 border border-white/10 text-white/60 hover:bg-white/20 transition-all active:scale-90"
          >
            <X size={18} />
          </button>
        </div>

        {/* Barra de progreso */}
        <div className="px-6 pb-4 shrink-0">
          <div className="flex justify-between text-[10px] font-black text-violet-300 mb-1.5">
            <span>🏆 Palabras recolectadas</span>
            <span>{collectedCount}/{totalQuestions}</span>
          </div>
          <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${progressPct}%`,
                background: 'linear-gradient(90deg, #a78bfa 0%, #c4b5fd 50%, #e9d5ff 100%)',
                boxShadow: '0 0 8px rgba(167,139,250,0.5)',
              }}
            />
          </div>
        </div>

        {/* Lista de categorías con palabras */}
        <div className="flex-1 overflow-y-auto px-4 pb-8 space-y-3">
          {collectedCount === 0 ? (
            <div className="text-center py-12">
              <p className="text-5xl mb-4">📭</p>
              <p className="text-violet-300 font-bold text-sm">Aún no tienes palabras.</p>
              <p className="text-violet-400 text-xs mt-1">¡Responde preguntas para desbloquearlas!</p>
            </div>
          ) : (
            Object.entries(COLLECTION_CATEGORIES).map(([catKey, catInfo]) => {
              const words = grouped[catKey] || [];
              if (words.length === 0) return null;
              const isExpanded = expandedCat === catKey;

              return (
                <div key={catKey} className="rounded-2xl overflow-hidden bg-white/5 border border-white/10">
                  {/* Header categoría */}
                  <button
                    onClick={() => setExpandedCat(isExpanded ? null : catKey)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{catInfo.emoji}</span>
                      <span className="text-white font-bold text-sm">{catInfo.label}</span>
                      <span className="bg-violet-500/30 text-violet-200 text-[10px] font-black px-2 py-0.5 rounded-full">
                        {words.length}
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp size={16} className="text-violet-400" />
                    ) : (
                      <ChevronDown size={16} className="text-violet-400" />
                    )}
                  </button>

                  {/* Chips de palabras (siempre visibles) */}
                  <div className="px-4 pb-3 flex flex-wrap gap-2">
                    {words.map(word => (
                      <button
                        key={word}
                        onClick={() => {
                          speak(word);
                          setExpandedCat(isExpanded && expandedCat === catKey ? null : catKey);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 border border-white/15 text-white text-xs font-bold hover:bg-white/20 transition-all active:scale-95"
                      >
                        <Volume2 size={10} className="text-violet-300" />
                        {word}
                      </button>
                    ))}
                  </div>

                  {/* Tarjetas expandidas */}
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2 border-t border-white/10 pt-3">
                      {words.map(word => <WordCard key={word} word={word} />)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
